import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Reveal } from '@/components/Reveal';
import { useResolvedPlans } from '@/hooks/usePlans';

export const PricingCards = ({ audience }) => {
  const { t } = useTranslation();
  const { plans } = useResolvedPlans();
  const [tab, setTab] = useState(audience || 'follower');
  const active = audience || tab;

  const AUDIENCES = [
    { key: 'follower', label: t('pricing.tabFollower') },
    { key: 'provider', label: t('pricing.tabProvider') },
  ];

  const list = plans
    .filter((p) => p.user_type === active && !/trial/i.test(p.name))
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

  return (
    <div>
      {!audience && (
        <div className="mb-8 flex justify-center">
          <div className="inline-flex rounded-full border border-white/10 bg-surface/60 p-1">
            {AUDIENCES.map((a) => (
              <button
                key={a.key}
                onClick={() => setTab(a.key)}
                data-testid={`pricing-tab-${a.key}`}
                className={`rounded-full px-5 py-2 text-sm font-semibold transition-all ${active === a.key ? 'bg-brand text-black' : 'text-white/60 hover:text-white'}`}
              >
                {a.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {!audience && (
        <p className="mb-10 text-center text-sm text-white/50">
          {active === 'follower' ? t('pricing.trialLineFollower') : t('pricing.trialLineProvider')}
        </p>
      )}

      {list.length === 0 ? (
        <p className="py-8 text-center text-sm text-white/40">{t('pricing.updating')}</p>
      ) : (
        <div className="mx-auto grid w-full gap-6 sm:grid-cols-2">
          {list.map((plan, i) => (
            <Reveal key={plan.id} delay={i * 0.1}>
              <div
                data-testid={`pricing-card-${active}-${i}`}
                className={`relative flex h-full flex-col rounded-3xl p-8 backdrop-blur-xl transition-all duration-300 ${
                  plan.is_featured
                    ? 'conic-border border border-brand/40 bg-surface shadow-[0_0_50px_-10px_rgba(23,253,253,0.45)] sm:-translate-y-3'
                    : 'border border-white/10 bg-surface/60 hover:-translate-y-1 hover:border-white/20'
                }`}
              >
                {plan.is_featured && (
                  <span className="absolute -top-3 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded-full bg-brand px-4 py-1 text-xs font-bold uppercase tracking-wider text-black shadow-[0_0_20px_rgba(23,253,253,0.5)]">
                    {t('common.mostPopular')}
                  </span>
                )}
                <h3 className="font-display text-sm font-bold uppercase tracking-[0.2em] text-white/90">{plan.name}</h3>
                <p className="mt-2 min-h-[40px] text-sm text-white/50">{plan.tagline}</p>
                <div className="mt-6 flex items-end gap-1">
                  <span className={`font-display text-6xl font-bold leading-none ${plan.is_featured ? 'text-brand' : 'text-white'}`}>${plan.price_monthly}</span>
                  <span className="mb-2 text-sm text-white/50">{t('common.perMonth')}</span>
                </div>
                <p className="mt-2 text-xs text-white/40">
                  {plan.price_yearly ? `${t('pricing.orYearly', { price: plan.price_yearly })} · ` : ''}{t('pricing.freeTrial')}
                </p>

                <Link
                  to="/contact"
                  data-testid={`pricing-cta-${active}-${i}`}
                  className={`mt-7 w-full justify-center ${plan.is_featured ? 'btn-primary' : 'btn-secondary'}`}
                >
                  {t('common.startFreeTrial')}
                </Link>

                <div className="my-7 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

                <ul className="space-y-3">
                  {plan.parsedFeatures.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-white/75">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      )}
    </div>
  );
};
